<template>
  <!-- The organization apps' install banner (Screenkode's look), for the
       admin: the same card, copy and behaviour, with the Screenkode k. -->
  <transition name="ska-pwa-slide">
    <div
      v-if="visible"
      class="ska-pwa-banner"
      :class="{ 'ska-pwa-banner--lifted': liftForBottomNav }"
      role="dialog"
      aria-label="Install Screenkode Admin app"
    >
      <button class="ska-pwa-close" aria-label="Dismiss" @click="dismiss">
        <i class="pi pi-times"></i>
      </button>

      <div class="ska-pwa-icon">
        <img :src="brandIcon" alt="Screenkode Admin" />
      </div>

      <div class="ska-pwa-body">
        <p class="ska-pwa-title">Install Screenkode Admin</p>

        <template v-if="mode === 'prompt'">
          <p class="ska-pwa-text">
            Add Screenkode Admin to your device for a faster, full-screen,
            app-like experience.
          </p>
          <div class="ska-pwa-actions">
            <button class="ska-pwa-btn ska-pwa-btn--primary" @click="install">
              Install app
            </button>
            <button class="ska-pwa-btn ska-pwa-btn--ghost" @click="dismiss">
              Not now
            </button>
          </div>
        </template>

        <template v-else-if="mode === 'ios'">
          <p class="ska-pwa-text">
            Tap
            <span class="ska-pwa-inline">
              <i class="pi pi-upload"></i> Share
            </span>
            then
            <span class="ska-pwa-inline">Add to Home Screen</span>
            to install.
          </p>
        </template>
      </div>
    </div>
  </transition>
</template>

<script>
import { DISPLAY_ROUTE } from '@/router/routes';
// The Screenkode k (logo-icon.png, as a vector so it is sharp at any size) —
// also the mark on the admin's app icons (public/icons).
import brandIcon from '@/assets/images/logo-icon.svg';
import {
  installState,
  isIosSafari,
  isStandalone,
  promptInstall,
} from '@/pwa/install_prompt';

const DISMISS_KEY = 'ska_pwa_install_dismissed_at';
// Once dismissed, stay quiet for this long before offering again.
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
// Let the app settle before the iOS hint appears — it is a static tip rather
// than a response to anything the browser told us, so it should not race the
// first paint.
const IOS_HINT_DELAY_MS = 2500;

export default {
  name: 'PwaInstallPrompt',
  props: {
    // Raises the banner clear of the phone bottom navigation when one is
    // showing (App.vue knows: it comes with the app shell).
    liftForBottomNav: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      brandIcon,
      installState,
      // 'ios' -> manual Safari hint. The native prompt mode is derived from the
      // captured `beforeinstallprompt` event instead (see `mode`).
      iosHint: false,
      dismissed: false,
      iosHintTimer: null,
    };
  },
  watch: {
    // Once the app is actually installed the stored "not now" is meaningless.
    // Clearing it means that if the user later uninstalls and comes back, the
    // offer returns straight away instead of being sat on for the rest of the
    // 7-day window.
    'installState.installed'(installed) {
      if (!installed) return;
      try {
        window.localStorage.removeItem(DISMISS_KEY);
      } catch (e) {
        /* ignore */
      }
    },
  },
  computed: {
    // 'prompt' -> native install available; 'ios' -> manual Safari hint.
    mode() {
      if (this.installState.canInstall) return 'prompt';
      if (this.iosHint) return 'ios';
      return null;
    },
    // Never over the Display: it is the unattended TV/kiosk view.
    onSuppressedRoute() {
      const route = this.$route;
      if (!route) return false;
      if (route.name === DISPLAY_ROUTE) return true;
      // Path fallback (`/display/:id`) so the banner is suppressed even if the
      // route name isn't resolved as expected.
      const path = String(route.path || '');
      return path === '/display' || path.startsWith('/display/');
    },
    visible() {
      // Reactive to route changes, so a prompt armed elsewhere stays hidden
      // while on the Display and reappears after leaving it.
      if (this.onSuppressedRoute) return false;
      if (this.installState.installed || this.dismissed) return false;
      return this.mode != null;
    },
  },
  methods: {
    recentlyDismissed() {
      try {
        const raw = window.localStorage.getItem(DISMISS_KEY);
        if (!raw) return false;
        const at = parseInt(raw, 10);
        if (!at) return false;
        return Date.now() - at < DISMISS_WINDOW_MS;
      } catch (e) {
        return false;
      }
    },
    rememberDismissal() {
      try {
        window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch (e) {
        /* ignore */
      }
    },
    async install() {
      const outcome = await promptInstall();
      // Only a real "no" earns the 7-day cooldown. 'accepted' needs no
      // bookkeeping — the `appinstalled` event flips installState.installed —
      // and 'unavailable' means the browser let us down, not the user, so the
      // offer should come back on the next visit rather than being buried.
      if (outcome === 'dismissed') this.rememberDismissal();
      this.dismissed = true;
    },
    dismiss() {
      this.rememberDismissal();
      this.dismissed = true;
    },
    maybeShowIosHint() {
      if (!isIosSafari()) return;
      this.iosHintTimer = window.setTimeout(() => {
        this.iosHintTimer = null;
        if (!this.installState.installed) this.iosHint = true;
      }, IOS_HINT_DELAY_MS);
    },
  },
  mounted() {
    if (typeof window === 'undefined') return;
    // A tab opened in standalone mode never sees `beforeinstallprompt`, but
    // re-check here in case the app was launched straight from the home screen.
    if (isStandalone()) {
      this.installState.installed = true;
      return;
    }
    if (this.recentlyDismissed()) {
      this.dismissed = true;
      return;
    }
    this.maybeShowIosHint();
  },
  beforeUnmount() {
    if (this.iosHintTimer != null) {
      window.clearTimeout(this.iosHintTimer);
      this.iosHintTimer = null;
    }
  },
};
</script>

<style scoped>
/* The Screenkode app's banner, value for value. */
.ska-pwa-banner {
  position: fixed;
  /* Above the phone bottom nav (900), below every overlay: modals and the side
     drawer are 1000, the bottom nav's "More" sheet and a support session's
     desktop 1200, the route spinner 9999. An opaque card pinned to the bottom
     edge must never cover a sheet's lower rows or a dialog's footer buttons;
     `--lifted` already keeps it off the bottom nav. */
  z-index: 950;
  left: 50%;
  bottom: 20px;
  transform: translateX(-50%);
  width: calc(100% - 32px);
  max-width: 420px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  background: #ffffff;
  border: 1px solid rgba(99, 102, 241, 0.18);
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.22);
  font-family: 'Poppins', system-ui, -apple-system, sans-serif;
}

/* The bottom nav is 64px tall plus the device's bottom safe area; clear both
   so the banner never sits on top of it. */
.ska-pwa-banner--lifted {
  bottom: calc(76px + env(safe-area-inset-bottom, 0px));
}

/* The bottom nav itself only renders below 992px, so above that the banner
   returns to the viewport bottom even inside the app shell. */
@media (min-width: 992px) {
  .ska-pwa-banner--lifted {
    bottom: 20px;
  }
}

.ska-pwa-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #94a3b8;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  line-height: 1;
}
.ska-pwa-close:hover {
  background: #f1f5f9;
  color: #475569;
}

.ska-pwa-icon {
  flex: 0 0 auto;
}
.ska-pwa-icon img {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: block;
  object-fit: contain;
  /* The k is a solid glyph, heavier than the app's line-drawn mark: inset it
     so it sits at the same size here as on its dark-mode plate below. */
  padding: 6px;
}

.ska-pwa-body {
  flex: 1 1 auto;
  min-width: 0;
  padding-right: 18px;
}

.ska-pwa-title {
  margin: 0 0 4px;
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
}

.ska-pwa-text {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.4;
  color: #475569;
}

.ska-pwa-inline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 7px;
  border-radius: 6px;
  background: #eef2ff;
  color: #4f46e5;
  font-weight: 600;
  white-space: nowrap;
}
.ska-pwa-inline .pi {
  font-size: 0.72rem;
}

.ska-pwa-actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.ska-pwa-btn {
  border: none;
  border-radius: 10px;
  padding: 9px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, opacity 0.15s ease;
}
.ska-pwa-btn--primary {
  background: #6366f1;
  color: #ffffff;
}
.ska-pwa-btn--primary:hover {
  background: #4f46e5;
}
.ska-pwa-btn--ghost {
  background: transparent;
  color: #64748b;
}
.ska-pwa-btn--ghost:hover {
  background: #f1f5f9;
}

/* slide-up transition */
.ska-pwa-slide-enter-active,
.ska-pwa-slide-leave-active {
  transition: transform 0.28s ease, opacity 0.28s ease;
}
.ska-pwa-slide-enter-from,
.ska-pwa-slide-leave-to {
  transform: translate(-50%, 24px);
  opacity: 0;
}

/* The admin ships a light theme only, so these are literals rather than tokens
   — they exist so the banner still reads correctly for a viewer whose OS is in
   dark mode and whose browser darkens the surrounding chrome. */
@media (prefers-color-scheme: dark) {
  .ska-pwa-banner {
    background: #1e293b;
    border-color: rgba(129, 140, 248, 0.3);
  }
  /* The k's stem is a dark navy drawn for light surfaces; a white plate keeps
     it legible against the dark banner (as Chabad Digital's banner does). */
  .ska-pwa-icon img {
    background: #ffffff;
  }
  .ska-pwa-title {
    color: #f8fafc;
  }
  .ska-pwa-text {
    color: #cbd5e1;
  }
  .ska-pwa-close {
    color: #94a3b8;
  }
  .ska-pwa-close:hover {
    background: #334155;
    color: #e2e8f0;
  }
  .ska-pwa-inline {
    background: #312e81;
    color: #c7d2fe;
  }
  .ska-pwa-btn--ghost {
    color: #cbd5e1;
  }
  .ska-pwa-btn--ghost:hover {
    background: #334155;
  }
}
</style>
